import { Container, Graphics, Sprite, Texture, Rectangle } from "pixi.js";

export class PuzzlePiece {
  public container: Container;
  public readonly id: number;
  public gridX: number;
  public gridY: number;
  public readonly correctGridX: number;
  public readonly correctGridY: number;

  private sprite: Sprite;
  private shadow: Graphics;
  private bevel: Graphics;
  private pieceWidth: number;
  private pieceHeight: number;
  private _isDragging: boolean = false;

  constructor(
    id: number,
    texture: Texture,
    gridX: number,
    gridY: number,
    correctGridX: number,
    correctGridY: number,
    pieceWidth: number,
    pieceHeight: number,
  ) {
    this.id = id;
    this.gridX = gridX;
    this.gridY = gridY;
    this.correctGridX = correctGridX;
    this.correctGridY = correctGridY;
    this.pieceWidth = pieceWidth;
    this.pieceHeight = pieceHeight;

    this.container = new Container();
    this.container.eventMode = "static";
    this.container.cursor = "pointer";
    this.container.hitArea = new Rectangle(0, 0, pieceWidth, pieceHeight);

    // Drop shadow (behind everything)
    this.shadow = new Graphics();
    this.container.addChild(this.shadow);

    // Piece visual
    this.sprite = new Sprite(texture);
    this.sprite.width = pieceWidth;
    this.sprite.height = pieceHeight;
    this.container.addChild(this.sprite);

    // 3D bevel effect (on top of sprite)
    this.bevel = new Graphics();
    this.container.addChild(this.bevel);

    // Draw the 3D effect
    this.draw3DEffect();

    // Set initial position
    this.updatePositionFromGrid();
  }

  public setPosition(x: number, y: number): void {
    this.container.x = x;
    this.container.y = y;
  }

  public updatePositionFromGrid(): void {
    this.container.x = this.gridX * this.pieceWidth;
    this.container.y = this.gridY * this.pieceHeight;
  }

  public animateToPosition(
    x: number,
    y: number,
    onComplete?: () => void,
  ): void {
    const duration = 200; // ms
    const startX = this.container.x;
    const startY = this.container.y;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);

      this.container.x = startX + (x - startX) * eased;
      this.container.y = startY + (y - startY) * eased;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else if (onComplete) {
        onComplete();
      }
    };

    animate();
  }

  public updateDimensions(width: number, height: number): void {
    this.pieceWidth = width;
    this.pieceHeight = height;

    // Update sprite size
    this.sprite.width = width;
    this.sprite.height = height;

    // Update hit area
    this.container.hitArea = new Rectangle(0, 0, width, height);

    // Redraw 3D effect
    this.draw3DEffect();
  }

  public startDrag(): void {
    this._isDragging = true;
    this.container.zIndex = 100; // Bring to front
    this.draw3DEffect(); // Redraw with lifted shadow
  }

  public endDrag(success: boolean): void {
    this._isDragging = false;
    this.container.zIndex = 1;
    this.draw3DEffect();
  }

  public flashCorrect(): void {
    // No flash effect
  }

  public playSnapAnimation(): void {
    // No animation
  }

  public hideEffects(): Promise<void> {
    return new Promise((resolve) => {
      const duration = 300;
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Fade out shadow and bevel
        this.shadow.alpha = 1 - progress;
        this.bevel.alpha = 1 - progress;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          this.shadow.visible = false;
          this.bevel.visible = false;
          resolve();
        }
      };

      animate();
    });
  }

  private connectedSides: Set<string> = new Set();

  public setConnected(
    side: "top" | "right" | "bottom" | "left",
    connected: boolean,
  ): void {
    if (connected) {
      this.connectedSides.add(side);
    } else {
      this.connectedSides.delete(side);
    }
    this.draw3DEffect();
  }

  public clearConnections(): void {
    this.connectedSides.clear();
    this.draw3DEffect();
  }

  public getConnectionCount(): number {
    return this.connectedSides.size;
  }

  private draw3DEffect(): void {
    const w = this.pieceWidth;
    const h = this.pieceHeight;
    const bevelSize = Math.max(2, Math.min(w, h) * 0.03); // 3% of piece size, min 2px
    const shadowOffset = this._isDragging ? 6 : 3;
    const shadowAlpha = this._isDragging ? 0.4 : 0.25;

    const hasTop = this.connectedSides.has("top");
    const hasRight = this.connectedSides.has("right");
    const hasBottom = this.connectedSides.has("bottom");
    const hasLeft = this.connectedSides.has("left");

    // Draw drop shadow only on unconnected edges
    this.shadow.clear();

    // Only draw shadow if we have at least one unconnected edge
    if (!hasTop || !hasRight || !hasBottom || !hasLeft) {
      // Bottom shadow (only if bottom not connected)
      if (!hasBottom) {
        const leftX = hasLeft ? 0 : 0;
        const rightX = hasRight ? w : w;
        this.shadow.moveTo(leftX, h);
        this.shadow.lineTo(rightX, h);
        this.shadow.lineTo(rightX + shadowOffset, h + shadowOffset);
        this.shadow.lineTo(leftX + shadowOffset, h + shadowOffset);
        this.shadow.closePath();
        this.shadow.fill({ color: 0x000000, alpha: shadowAlpha });
      }

      // Right shadow (only if right not connected)
      if (!hasRight) {
        const topY = hasTop ? 0 : 0;
        const bottomY = hasBottom ? h : h;
        this.shadow.moveTo(w, topY);
        this.shadow.lineTo(w + shadowOffset, topY + shadowOffset);
        this.shadow.lineTo(w + shadowOffset, bottomY + shadowOffset);
        this.shadow.lineTo(w, bottomY);
        this.shadow.closePath();
        this.shadow.fill({ color: 0x000000, alpha: shadowAlpha });
      }
    }

    // Draw bevel effect
    this.bevel.clear();

    // Top highlight (light coming from top-left)
    if (!hasTop) {
      const leftX = hasLeft ? 0 : bevelSize;
      const rightX = hasRight ? w : w - bevelSize;
      this.bevel.moveTo(0, 0);
      this.bevel.lineTo(w, 0);
      this.bevel.lineTo(rightX, bevelSize);
      this.bevel.lineTo(leftX, bevelSize);
      this.bevel.closePath();
      this.bevel.fill({ color: 0xffffff, alpha: 0.4 });
    }

    // Left highlight
    if (!hasLeft) {
      const topY = hasTop ? 0 : bevelSize;
      const bottomY = hasBottom ? h : h - bevelSize;
      this.bevel.moveTo(0, 0);
      this.bevel.lineTo(bevelSize, topY);
      this.bevel.lineTo(bevelSize, bottomY);
      this.bevel.lineTo(0, h);
      this.bevel.closePath();
      this.bevel.fill({ color: 0xffffff, alpha: 0.25 });
    }

    // Bottom shadow (darker edge)
    if (!hasBottom) {
      const leftX = hasLeft ? 0 : bevelSize;
      const rightX = hasRight ? w : w - bevelSize;
      this.bevel.moveTo(0, h);
      this.bevel.lineTo(leftX, h - bevelSize);
      this.bevel.lineTo(rightX, h - bevelSize);
      this.bevel.lineTo(w, h);
      this.bevel.closePath();
      this.bevel.fill({ color: 0x000000, alpha: 0.3 });
    }

    // Right shadow
    if (!hasRight) {
      const topY = hasTop ? 0 : bevelSize;
      const bottomY = hasBottom ? h : h - bevelSize;
      this.bevel.moveTo(w, 0);
      this.bevel.lineTo(w, h);
      this.bevel.lineTo(w - bevelSize, bottomY);
      this.bevel.lineTo(w - bevelSize, topY);
      this.bevel.closePath();
      this.bevel.fill({ color: 0x000000, alpha: 0.2 });
    }

    // Outer border only on unconnected sides
    // Scale border width for visibility on small pieces
    const borderWidth = Math.max(1, Math.min(w, h) * 0.02);
    const borderAlpha = 0.25;

    if (!hasTop) {
      this.bevel.moveTo(0, 0);
      this.bevel.lineTo(w, 0);
      this.bevel.stroke({
        width: borderWidth,
        color: 0x000000,
        alpha: borderAlpha,
      });
    }
    if (!hasRight) {
      this.bevel.moveTo(w, 0);
      this.bevel.lineTo(w, h);
      this.bevel.stroke({
        width: borderWidth,
        color: 0x000000,
        alpha: borderAlpha,
      });
    }
    if (!hasBottom) {
      this.bevel.moveTo(w, h);
      this.bevel.lineTo(0, h);
      this.bevel.stroke({
        width: borderWidth,
        color: 0x000000,
        alpha: borderAlpha,
      });
    }
    if (!hasLeft) {
      this.bevel.moveTo(0, h);
      this.bevel.lineTo(0, 0);
      this.bevel.stroke({
        width: borderWidth,
        color: 0x000000,
        alpha: borderAlpha,
      });
    }
  }
}
